/**
 * Finance Investigation contracts (IBA-75 — the investigation experience).
 *
 * These describe the READ side of Finance Audit as an *investigation tool*: the Explorer queries the
 * financial reality that already exists (posted transactions ↔ their journal entries ↔ the general ledger)
 * and attaches the audit / reconciliation / receipt / reversal layer to each event. This is deliberately
 * distinct from the append-only {@link ./finance-audit finance_audit_logs} feed — the audit log is the
 * *who / why / what-changed* evidence surfaced INSIDE an investigation, not the list itself.
 *
 * Enum-valued wire fields are typed as `string` here (DTO shapes, not value-set declarations); the API
 * resolves them from the real Prisma enums, and the tenant maps them to its shared enums when rendering.
 */

import type { EntityInvestigation } from "../investigation/entity-investigation";
import { FinanceTransactionType } from "./finance.enums";

/** One affected accounting account on a financial event (from the event's journal lines). */
export interface FinanceInvestigationEventAccount {
  accountingAccountId: string;
  code: string;
  name: string;
  /** Optional full i18n key for seeded chart-of-accounts names. */
  translationKey?: string | null;
  direction: string; // DEBIT | CREDIT (JournalLineDirection)
  amount: number;
}

/** Derived evidence state for a financial event (V1: an issued receipt or a payment reference). */
export interface FinanceInvestigationEvidence {
  hasReceipt: boolean;
  receiptId: string | null;
  receiptNumber: string | null;
  receiptStatus: string | null; // ReceiptStatus (ISSUED | CANCELLED) or null
  hasPaymentReference: boolean;
  /** True when the event has usable evidence: a non-cancelled receipt OR a payment reference. */
  present: boolean;
}

/**
 * A single row of an investigation result — a posted financial event (a transaction ↔ its journal entry),
 * enriched with everything an investigator needs to triage without opening the detail.
 */
export interface FinanceInvestigationEvent {
  transactionId: string;
  reference: string | null;
  type: string; // FinanceTransactionType (INCOME | EXPENSE | TRANSFER_IN | TRANSFER_OUT)
  kind: string; // FinanceTransactionKind (NORMAL | REVERSAL)
  status: string; // FinanceTransactionStatus (ACTIVE | VOIDED | CANCELLED)
  amount: number;
  currency: string | null;
  occurredAt: string;
  description: string | null;
  actorUserId: string | null;
  actorName: string | null;
  actorAvatarUrl: string | null;
  branchId: string;
  branchName: string | null;
  journalEntryId: string | null;
  journalStatus: string | null; // JournalEntryStatus (POSTED | REVERSED) or null
  accounts: FinanceInvestigationEventAccount[];
  fundId: string | null;
  fundName: string | null;
  activityId: string | null;
  activityTitle: string | null;
  budgetLineId: string | null;
  /**
   * The money account the event moved money in or out of (a bank, a cash box), by name. For a transfer leg it is
   * the side this branch holds. Null when the transaction names none (legacy rows).
   */
  moneyAccountId: string | null;
  moneyAccountName: string | null;
  reconciled: boolean;
  evidence: FinanceInvestigationEvidence;
  /** The original was corrected by a reversal (status CANCELLED / VOIDED). */
  isReversed: boolean;
  /** If this is a reversal row, the transaction it reverses. */
  reversesTransactionId: string | null;
  /** The reversal transaction that cancelled this one, when reversed. */
  reversalTransactionId: string | null;
}

/** Aggregate summary of an investigation result set — the "answer" line above the table. */
export interface FinanceInvestigationAggregate {
  count: number;
  /** Unsigned sum of every matching amount (kept for existing readers; not a net). */
  totalAmount: number;
  /** Money in: offerings and incoming transfer legs ({@link FinanceInvestigationFlow.In}). */
  inflowCount: number;
  inflowAmount: number;
  /** Money out: expenses and outgoing transfer legs ({@link FinanceInvestigationFlow.Out}). */
  outflowCount: number;
  outflowAmount: number;
  /** `inflowAmount - outflowAmount`. Internal transfers move money between two own accounts and count in neither. */
  netAmount: number;
}

/** Which way a financial event moved the branch's money. */
export enum FinanceInvestigationFlow {
  In = "IN",
  Out = "OUT",
  /** An internal transfer between two of the branch's own accounts: nothing entered or left. */
  Internal = "INTERNAL",
}

/** The flow of a transaction type, shared by the API totals and the tenant rows so the two can never disagree. */
export function financeInvestigationFlow(type: string): FinanceInvestigationFlow {
  if (type === FinanceTransactionType.Income || type === FinanceTransactionType.TransferIn) {
    return FinanceInvestigationFlow.In;
  }
  if (type === FinanceTransactionType.Expense || type === FinanceTransactionType.TransferOut) {
    return FinanceInvestigationFlow.Out;
  }
  return FinanceInvestigationFlow.Internal;
}

export interface FinanceInvestigationEventsResponse {
  items: FinanceInvestigationEvent[];
  total: number;
  page: number;
  limit: number;
  aggregate: FinanceInvestigationAggregate;
}

/* ------------------------------------------------------------------ */
/* Investigation detail (the workspace)                                */
/* ------------------------------------------------------------------ */

export interface FinanceInvestigationRelatedRef {
  type: string;
  id: string;
  label: string | null;
}

export interface FinanceInvestigationJournalLine {
  direction: string;
  amount: number;
  accountingAccountId: string;
  code: string;
  name: string;
  type: string; // AccountingAccountType
}

export interface FinanceInvestigationJournal {
  id: string;
  status: string;
  entryDate: string;
  postedAt: string | null;
  currency: string | null;
  reversesEntryId: string | null;
  reversedByEntryId: string | null;
  totalDebit: number;
  totalCredit: number;
  lines: FinanceInvestigationJournalLine[];
}

export interface FinanceInvestigationReceipt {
  id: string;
  receiptNumber: string;
  status: string;
  amount: number;
  currency: string | null;
  issuedByUserId: string | null;
  issuedByName: string | null;
  issuedAt: string;
  cancelledAt: string | null;
  cancelledReason: string | null;
}

export interface FinanceInvestigationReconciliation {
  reconciled: boolean;
  sessions: Array<{
    sessionId: string;
    status: string;
    reconciled: boolean;
    statementDateFrom: string | null;
    statementDateTo: string | null;
  }>;
}

export interface FinanceInvestigationTimelineItem {
  id: string;
  action: string;
  actionLabel: string;
  createdAt: string;
  reason: string | null;
  actorName: string | null;
}

export interface FinanceInvestigationSource {
  transactionId: string;
  reference: string | null;
  type: string;
  kind: string;
  status: string;
  amount: number;
  currency: string | null;
  occurredAt: string;
  description: string | null;
  paymentMethod: string | null;
  paymentReference: string | null;
  actorUserId: string | null;
  actorName: string | null;
  branchId: string;
  branchName: string | null;
}

/** The full investigation workspace for one financial object — a navigable graph, not a leaf. */
export interface FinanceInvestigationDetail {
  source: FinanceInvestigationSource;
  journal: FinanceInvestigationJournal | null;
  receipt: FinanceInvestigationReceipt | null;
  reconciliation: FinanceInvestigationReconciliation;
  evidence: FinanceInvestigationEvidence;
  related: FinanceInvestigationRelatedRef[];
  /** Reversal chain: the transaction this reverses, and the reversal that cancelled this. */
  reversal: {
    reversesTransactionId: string | null;
    reversesReference: string | null;
    reversalTransactionId: string | null;
    reversalReference: string | null;
  };
  timeline: FinanceInvestigationTimelineItem[];
}

/* ------------------------------------------------------------------ */
/* Landing signals ("Needs attention")                                 */
/* ------------------------------------------------------------------ */

export interface FinanceInvestigationSignal {
  code: string;
  count: number;
}

export interface FinanceInvestigationSignalsResponse {
  /** ACTIVE income/expense transactions with no issued receipt and no payment reference. */
  missingEvidence: FinanceInvestigationSignal;
  /** Transactions corrected by a reversal (status CANCELLED). */
  reversed: FinanceInvestigationSignal;
  /** Voided transactions. */
  voided: FinanceInvestigationSignal;
}

/** The kind of object an investigation detail can be anchored on. */
export enum FinanceInvestigationKind {
  Transaction = "transaction",
  Journal = "journal",
}

/* ------------------------------------------------------------------ */
/* Transaction fact sheet (the I2 investigation layout)                */
/* ------------------------------------------------------------------ */

/** Where a transaction's donor receipt stands. `None` = never issued. */
export enum FinanceInvestigationReceiptState {
  Issued = "ISSUED",
  Cancelled = "CANCELLED",
  None = "NONE",
}

/**
 * Whether the transaction's treasury account has been reconciled THROUGH the operation date.
 *
 * `Covered`: an approved reconciliation reaches the operation date (or cleared this very line).
 * `NotCovered`: the account was reconciled, but only up to an earlier date.
 * `Never`: the account has no approved reconciliation at all.
 */
export enum FinanceInvestigationReconciliationState {
  Covered = "COVERED",
  NotCovered = "NOT_COVERED",
  Never = "NEVER",
}

/**
 * The structured facts behind a transaction investigation, so the page can state them in the reader's language
 * instead of parsing English labels. Carries NO amount beyond what the investigation header already discloses:
 * it is built after the same access + disclosure check (ADR-0024) as the rest of the detail.
 */
export interface FinanceTransactionInvestigationFacts {
  transactionId: string;
  reference: string | null;
  /** FinanceTransactionType. */
  type: string;
  /** FinanceTransactionKind. */
  kind: string;
  /** FinanceTransactionStatus. */
  status: string;
  /** When the money actually moved. */
  occurredAt: string;
  /** When the transaction was entered in Ibada (createdAt). */
  recordedAt: string;
  recordedBy: { userId: string | null; name: string } | null;
  /** FinancePaymentMethod, or null when none was recorded. */
  paymentMethod: string | null;
  paymentReference: string | null;
  /** What a person typed, never the `REVERSAL_OF:` pointer. */
  description: string | null;
  branchName: string | null;
  /** The workspace system branch: the client names it in the reader's language ("Paroisse"). */
  branchIsParish: boolean;
  /** The income/expense account the transaction records against. */
  accountingAccount: { id: string; code: string | null; name: string; translationKey?: string | null } | null;
  /** The treasury (cash/bank/mobile money) account the money sits in. */
  financeAccount: { id: string; name: string } | null;
  member: { id: string; name: string } | null;
  /** Supporting evidence, on the exact predicate of the "missing evidence" signal. */
  evidence: { applicable: boolean; present: boolean };
  receipt: {
    /** A donor receipt only makes sense for live, normal income. */
    applicable: boolean;
    state: FinanceInvestigationReceiptState;
    receiptNumber: string | null;
    issuedAt: string | null;
  };
  /** Null when the transaction has no treasury account (nothing to reconcile). */
  reconciliation: {
    state: FinanceInvestigationReconciliationState;
    /** The statement date the latest approved reconciliation reached. */
    reconciledThrough: string | null;
  } | null;
}

/** A finance investigation: the generic workspace contract plus, for a transaction, its structured facts. */
export interface FinanceEntityInvestigation extends EntityInvestigation {
  transactionFacts?: FinanceTransactionInvestigationFacts | null;
}
